import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Message } from '../types';

type Props = {
  messages: Message[];
  typing: boolean;
  disabled: boolean;
  choices: string[] | null;
  onSend: (text: string) => void;
};

export default function ChatPanel({ messages, typing, disabled, choices, onSend }: Props) {
  const [input, setInput] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = scrollRef.current;
    if (el && typeof el.scrollTo === 'function') {
      el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    }
  }, [messages, typing]);

  const submit = (e?: React.FormEvent) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text) return;
    onSend(text);
    setInput('');
  };

  return (
    <div className="flex flex-col min-h-0 flex-1 bg-bg border-b lg:border-b-0 lg:border-r border-border">
      <div className="h-12 flex items-center justify-between px-4 border-b border-border shrink-0">
        <div className="flex items-center gap-2.5">
          <span className="w-6 h-6 rounded-md bg-accentSoft border border-accentLine flex items-center justify-center">
            <i className="ri-stack-line text-accent text-sm" />
          </span>
          <span className="text-sm font-semibold text-textp">Sourcetape Composer</span>
        </div>
        <Link
          to="/"
          className="flex items-center gap-1 text-xs text-texts hover:text-textp transition-colors"
        >
          <i className="ri-arrow-left-line" />
          <span className="hidden sm:inline">Home</span>
        </Link>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-5 space-y-3" data-testid="composer-chat">
        {messages.map((m) => (
          <div key={m.id} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div
              className={`max-w-[80%] rounded-lg px-3.5 py-2.5 text-sm leading-relaxed ${
                m.role === 'user'
                  ? 'bg-accent text-accent-text'
                  : 'bg-surface border border-border text-textp'
              }`}
            >
              <div className="whitespace-pre-wrap">{m.text}</div>
              <div className={`mt-1 text-[10px] opacity-60 ${m.role === 'user' ? 'text-accent-text' : 'text-texts'}`}>
                {m.time}
              </div>
            </div>
          </div>
        ))}

        {typing && (
          <div className="flex justify-start">
            <div className="bg-surface border border-border rounded-lg px-3.5 py-3 flex items-center gap-1.5">
              <span className="typing-dot" />
              <span className="typing-dot" />
              <span className="typing-dot" />
            </div>
          </div>
        )}
      </div>

      {choices && !typing && !disabled && (
        <div className="px-4 pb-2 flex flex-wrap gap-2 shrink-0" data-testid="composer-choices">
          {choices.map((c) => (
            <button
              key={c}
              type="button"
              data-testid={`composer-choice-${c}`}
              onClick={() => onSend(c)}
              className="whitespace-nowrap text-xs bg-surface hover:bg-surface2 border border-border hover:border-accentLine rounded-full px-3 py-1.5 text-textp transition-colors"
            >
              {c}
            </button>
          ))}
        </div>
      )}

      <div className="p-3 border-t border-border shrink-0">
        <form
          onSubmit={submit}
          className="flex items-center gap-2 bg-surface border border-border rounded-lg pl-3 pr-1.5 py-1.5 focus-within:border-accent"
        >
          <input
            data-testid="composer-chat-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={disabled ? 'Brief complete' : 'Type your answer…'}
            disabled={disabled}
            className="flex-1 bg-transparent text-sm text-textp placeholder:text-texts outline-none disabled:opacity-50"
          />
          <button
            type="submit"
            data-testid="composer-chat-send"
            disabled={disabled || !input.trim()}
            className="flex items-center justify-center w-8 h-8 rounded-md bg-accent hover:bg-accent-hover text-accent-text disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            aria-label="Send"
          >
            <i className="ri-arrow-up-line" />
          </button>
        </form>
      </div>
    </div>
  );
}
